import React from 'react';
import { Alert, Box, Grid, Stack, Typography } from '@mui/material';
import { CheckCircle, FilterAlt, Input, Output, Tune } from '@mui/icons-material';
import { useEnergy } from '../context/EnergyContext';
import { formatValue } from '../utils/energyViewModel';
import { EnergyLineChart, EnergyPageHeader, MetricTile, SectionCard } from '../components/energy/EnergyUi';

const DispatchStage = ({ number, icon, title, value, detail, color = 'primary.main' }) => (
  <Box sx={{ display: 'flex', gap: 1.25, p: 1.5, border: '1px solid', borderColor: 'divider', borderLeft: 4, borderLeftColor: color, bgcolor: 'background.paper' }}>
    <Box sx={{ color, mt: 0.25 }}>{icon}</Box>
    <Box><Typography variant="caption" color="text.secondary">{number}. {title}</Typography><Typography variant="body1" fontWeight={700}>{value}</Typography><Typography variant="caption" color="text.secondary">{detail}</Typography></Box>
  </Box>
);

export default function EmsView() {
  const { model } = useEnergy();
  const { ems } = model;
  return (
    <Box>
      <EnergyPageHeader title="EMS Operational View" subtitle="Read-only operating intent, constraint application, acknowledgement, and measured outcome" back />
      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Stack spacing={2}>
            <SectionCard title="Real-time dispatch pipeline" subtitle="Each stage is retained so command intent is not confused with measured response">
              <Stack spacing={1}>
                <DispatchStage number="1" icon={<Input />} title="Inputs" value={`Solar ${formatValue(model.power.solarKw, 'kW')} · Load ${formatValue(model.power.loadKw, 'kW')}`} detail={`Grid ${formatValue(model.power.gridKw, 'kW')} · SOC ${formatValue(model.bess.socPct, '%')}`} />
                <DispatchStage number="2" icon={<Tune />} title="Objective" value={ems.objective || '-'} detail={`Mode: ${ems.mode || '-'}`} />
                <DispatchStage number="3" icon={<Output />} title="Raw controller request" value={formatValue(ems.requestedKw, 'kW')} detail="Pre-constraint request" />
                <DispatchStage number="4" icon={<FilterAlt />} title="Binding constraints" value={ems.constraint || '-'} detail={`Constrained request: ${formatValue(ems.constrainedKw, 'kW')}`} color="warning.main" />
                <DispatchStage number="5" icon={<CheckCircle />} title="Command and response" value={formatValue(ems.actualKw, 'kW')} detail={`Acknowledgement: ${ems.acknowledgement || '-'}`} color="success.main" />
              </Stack>
            </SectionCard>
            <SectionCard title="Decision rationale and control context">
              <Alert severity={ems.reason ? 'info' : 'warning'}>{ems.reason || '-'}</Alert>
              <Typography variant="body2" sx={{ mt: 1.5 }}>Authority: {ems.authority || '-'}</Typography>
              <Typography variant="body2">Demand target: {formatValue(ems.demandTargetKw, 'kW')}</Typography>
              <Typography variant="body2">Grid export limit: {formatValue(ems.exportLimitKw, 'kW')}</Typography>
            </SectionCard>
            <SectionCard title="Aligned site power and dispatch history">
              <EnergyLineChart series={model.series} fields={[{ key: 'requestedKw', label: 'Requested', color: '#1565c0' }, { key: 'constrainedKw', label: 'Constrained', color: '#ef6c00' }, { key: 'pcsKw', label: 'Actual PCS response', color: '#388e3c' }]} height={260} />
            </SectionCard>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            <SectionCard title="Site algebraic reconciliation">
              <Grid container spacing={1}>
                <Grid item xs={6}><MetricTile label="Solar" value={model.power.solarKw} unit="kW" accent="#f57c00" /></Grid>
                <Grid item xs={6}><MetricTile label="Load" value={model.power.loadKw} unit="kW" accent="#7b1fa2" /></Grid>
                <Grid item xs={6}><MetricTile label="Battery" value={model.power.bessKw} unit="kW" accent="#388e3c" /></Grid>
                <Grid item xs={6}><MetricTile label="Grid" value={model.power.gridKw} unit="kW" accent="#1976d2" /></Grid>
                <Grid item xs={12}><MetricTile label="Residual" value={model.power.residualKw} unit="kW" accent={model.power.balanced === false ? '#ed6c02' : '#388e3c'} /></Grid>
              </Grid>
            </SectionCard>
            <SectionCard title="Active policy and interlocks">
              <Typography variant="body2">Minimum SOC: {formatValue(model.bess.minSocPct, '%')}</Typography>
              <Typography variant="body2">Maximum SOC: {formatValue(model.bess.maxSocPct, '%')}</Typography>
              <Typography variant="body2">Available charge: {formatValue(model.bess.availableChargeKw, 'kW')}</Typography>
              <Typography variant="body2">Available discharge: {formatValue(model.bess.availableDischargeKw, 'kW')}</Typography>
              <Alert severity="info" sx={{ mt: 1.5 }}>This view exposes operating state only. It does not issue commands or change dispatch policy.</Alert>
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
